import { FiArrowLeft, FiRotateCw, FiTrash2 } from "react-icons/fi";

interface TrashHeaderProps {
  /** page title */
  title: string;
  /** item count for display */
  itemCount: number;
  /** number of selected items */
  selectedCount: number;
  /** label for the back button (e.g., "Back to Audio", "Back to Phonics") */
  backLabel: string;
  /** callback when back button is clicked */
  onBack: () => void;
  /** callback when bulk restore is clicked */
  onBulkRestore: () => void;
  /** callback when bulk delete is clicked */
  onBulkDelete: () => void;
  /** whether any processing is happening (disables actions) */
  isProcessing?: boolean;
}

/**
 * Generic trash page header with back button and bulk actions
 * Used by TrashView and PhonicsTrashPage
 */
export function TrashHeader({
  title,
  itemCount,
  selectedCount,
  backLabel,
  onBack,
  onBulkRestore,
  onBulkDelete,
  isProcessing = false,
}: TrashHeaderProps) {
  return (
    <div className="border-b border-gray-200 bg-white px-6 py-4">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
      >
        <FiArrowLeft className="h-4 w-4" />
        {backLabel}
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {itemCount} {itemCount === 1 ? "item" : "items"} in trash
          </p>
        </div>

        {/* Bulk Actions */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBulkRestore}
              disabled={isProcessing}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <FiRotateCw className="h-4 w-4" />
              Restore ({selectedCount})
            </button>
            <button
              type="button"
              onClick={onBulkDelete}
              disabled={isProcessing}
              className="flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <FiTrash2 className="h-4 w-4" />
              Delete permanently ({selectedCount})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
